import React, { useEffect, lazy, Suspense } from "react";
import {
  CurrencyDollarIcon,
  CalendarIcon,
  ArrowsRightLeftIcon,
  UserIcon,
  CameraIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
// import PayrollWebApp1 from "@/assets/waggex-payroll-web-app-dashboard.webp?w=400;600&format=webp";
import PayrollWebApp from "@/assets/waggex-employee-onboarding-setup-india600.webp?w=350&format=webp";
import PayrollWebAppdesktop from "@/assets/waggex-employee-onboarding-setup-india600.webp?w=400&format=webp";
import PayrollWebApp2 from "@/assets/waggex-employee-onboarding-setup-india.webp?w=400;600&format=webp";
import PayrollWebApp3 from "@/assets/waggex-attendance-digital-records-payroll.webp?w=400;600&format=webp";
import PayrollWebApp4 from "@/assets/waggex-payroll-reminders-document-generation600.webp?w=350&format=webp";
import PayrollWebApp4desktop from "@/assets/waggex-payroll-reminders-document-generation600.webp?w=400&format=webp";

import HRFeatureComparison from "./payrolewebapp/HRFeatureComparison";
import { Link } from "react-router-dom";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("../pages/HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
import { Helmet } from "react-helmet-async";
import { useInView } from "react-intersection-observer";

import { useHrmsCounts } from "../hooks/useHrmsCounts";
import StatsSection from "./common/StatsSection";

const payrollfaq1 = [
  {
    title: "What is HRMS software and how does it differ from basic HR tools?",
    content:
      "HRMS stands for Human Resource Management System, a complete platform that manages the full employee lifecycle from onboarding to exit. Unlike basic HR tools that only handle one function, Waggex HRMS integrates payroll, attendance, leave management, compliance, employee self-service, and HR reporting in a single system.",
  },
  {
    title: "Does Waggex HRMS handle Indian statutory compliance automatically?",
    content:
      "Yes. Waggex HRMS auto-calculates PF, ESI, TDS, and Professional Tax based on each employee's salary structure. It sends reminders for filing deadlines, generates statutory challans, and keeps rules updated with EPFO and Income Tax notifications so compliance is never an afterthought.",
  },
  {
    title: "Can Waggex HRMS manage leave, shifts, and attendance together?",
    content:
      "Yes. Waggex HRMS connects leave, shift rosters, and attendance into one unified module. Leave balances update in real time, approved leaves are excluded from attendance, and shift data feeds directly into payroll with zero manual reconciliation.",
  },
];

const payrollfaq2 = [
  {
    title: "Is Waggex HRMS suitable for multi-location businesses in India?",
    content:
      "Absolutely. Waggex is built for businesses with teams spread across cities, branches, and remote locations. Employees mark attendance from any location using the selfie app or desktop portal. HR admins get consolidated dashboards and payroll runs centrally regardless of how many locations you operate.",
  },
  {
    title: "How does the employee self-service portal work in Waggex HRMS?",
    content:
      "Employees access their own data through the Waggex Staff App or web portal. They can view payslips, apply for leave, check attendance history, submit expense claims, and access documents like offer letters and Form 16 without raising a single HR ticket.",
  },
  {
    title: "How long does it take to get started with Waggex HRMS?",
    content:
      "Most businesses are fully operational on Waggex HRMS within a day. Company setup, department configuration, and employee bulk import can all be done through the guided onboarding flow. No IT team, no lengthy implementation, and no data migration headaches.",
  },
];
const headers = ["FEATURES", "WITHOUT HRMS", "WITH WAGGEX HRMS"];
const rows = [
  {
    feature: "Employee Onboarding",
    without: "Weeks of paperwork",
    with: "Live in under 1 day",
  },
  {
    feature: "Leave Management",
    without: "WhatsApp, manual Excel",
    with: "Automated, real-time",
  },
  {
    feature: "Payroll Processing",
    without: "2–3 days, error-prone",
    with: "Under 2 minutes",
  },
  {
    feature: "PF / ESI / TDS Filing",
    without: "Missed deadlines, fines",
    with: "Auto-reminders, done",
  },
  {
    feature: "HR Reporting",
    without: "Days to compile manually",
    with: "Live dashboards, 1 click",
  },
  {
    feature: "Compliance Tracking",
    without: "Reactive, always at risk",
    with: "Proactive, always ready",
  },
  {
    feature: "Exit & F&F Settlement",
    without: "Weeks of manual effort",
    with: "Documented, automated",
  },
];

const schema = [
  // SCHEMA 1: SoftwareApplication (HRMS)
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex HRMS",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    description:
      "Waggex is a complete HRMS software for Indian businesses integrating payroll, attendance, leave management, compliance, employee self-service, and HR analytics in one cloud-based platform.",
    url: "https://www.waggex.com/hrms-software",
    screenshot:
      "https://www.waggex.com/waggex-employee-onboarding-setup-india.webp",
    featureList: [
      "HRMS software India",
      "Automated payroll and PF ESI TDS",
      "Leave management software India",
      "Attendance tracking HRMS",
      "Employee self-service portal",
      "HR analytics and compliance reporting",
      "Employee lifecycle management",
      "Cloud HRMS India",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      description: "Free trial available no credit card required",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "341",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is HRMS software and how does it differ from basic HR tools?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "HRMS is a complete Human Resource Management System that manages the full employee lifecycle including payroll, attendance, leave, compliance, employee self-service, and HR reporting in one platform.",
        },
      },
      {
        "@type": "Question",
        name: "Does Waggex HRMS handle Indian statutory compliance automatically?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex automatically calculates PF, ESI, TDS, and Professional Tax, generates challans, and keeps compliance aligned with Indian regulations.",
        },
      },
      {
        "@type": "Question",
        name: "Can Waggex HRMS manage leave, shifts, and attendance together?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Leave, shifts, and attendance are fully integrated, ensuring real-time updates and automatic payroll processing without manual work.",
        },
      },
      {
        "@type": "Question",
        name: "Is Waggex HRMS suitable for multi-location businesses in India?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex supports multi-location and remote teams with centralized dashboards and unified payroll management.",
        },
      },
      {
        "@type": "Question",
        name: "How does the employee self-service portal work in Waggex HRMS?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Employees can access payslips, apply for leave, view attendance, submit claims, and download documents through the mobile app or web portal.",
        },
      },
      {
        "@type": "Question",
        name: "How long does it take to get started with Waggex HRMS?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most businesses can go live within a day using guided onboarding and bulk employee import tools.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides complete HRMS software for Indian businesses payroll, attendance, leave, compliance, and employee self-service in one cloud platform.",
    areaServed: "IN",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
    sameAs: [
      "https://www.linkedin.com/company/waggex",
      "https://twitter.com/waggex",
      "https://www.facebook.com/waggex",
    ],
  },
];
const dashboardFeatures = [
  { icon: CurrencyDollarIcon, label: "Payroll Management" },
  { icon: CalendarIcon, label: "Attendance Management" },
  { icon: ArrowsRightLeftIcon, label: "Leave & Shift Management" },
  { icon: UserIcon, label: "Staff App (Employees)" },
  { icon: CameraIcon, label: "Selfie Attendance" },
  { icon: DocumentTextIcon, label: "Payslip Generation" },
];

export default function HRMSSoftware() {
  const handleRedirect = () => {
    window.location.href = "https://app.waggex.com/signup";
  };
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>HRMS Software For Payroll & Employee Management</title>
        <meta
          name="description"
          content="Waggex HRMS software automates payroll, attendance, leave, and compliance for Indian businesses. Manage your full workforce from one platform. Free trial, no credit card."
        />
        <meta
          name="keywords"
          content="HRMS software India HRMS software,HR management software India,cloud HRMS India,HRMS payroll software India,HR software for small business India,employee management software India,leave management software India,HR compliance software India,HRMS attendance management India"
        />
        <link rel="canonical" href="https://www.waggex.com/hrms-software" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section ref={ref}>
        <section className="bg-[#d1dfea] py-10 px-6">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-8">
            {/* Left Content */}
            <div className="lg:w-[48%] text-center lg:text-left min-h-100">
              <span className="inline-block px-8 py-2 border border-[#10324F] rounded-xl text-[#10324F] text-sm font-medium">
                HRMS Software
              </span>

              <h1 className="mt-8 text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[28px]">
                India’s Complete HRMS Software Platform
              </h1>

              <p className="service-list-item-text py-4 px-2">
                Stop managing HR with five different tools and three Excel
                sheets. Waggex HRMS software brings payroll, attendance, leave
                management, statutory compliance, employee self-service, and HR
                reporting into one seamless platform built specifically for how
                Indian businesses operate
              </p>

              <button
                className="mt-4 bg-[#10324F] text-white h-12.5 px-14 rounded-xl text-xl shadow-lg hover:scale-[1.02] transition"
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
              >
                Get Started Free
              </button>
            </div>

            {/* Right Dashboard Card */}
            <div className="lg:w-[52%]">
              <div className="rounded-2xl p-4  border-2 border-white">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-roboto font-medium text-gray-700">
                    Waggex HRMS - Dashboard
                  </h2>

                  <p className="flex items-center gap-1">
                    <span className="text-green-700">●</span>
                    <span className="text-[#10324F] text-sm font-roboto font-bold">
                      Live
                    </span>
                  </p>
                </div>

                {/* Real Counts */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 text-center w-full">
                  {[
                    {
                      value: loading
                        ? "..."
                        : `${counts.permanentCompanyCount}+`,
                      label: "Total Companies",
                    },
                    {
                      value: loading
                        ? "..."
                        : `${counts.permanentEmployeeCount}+`,
                      label: "Total Employees",
                    },

                    {
                      value: loading
                        ? "..."
                        : `${counts.permanentPayslipCount}+`,
                      label: "Payslips Generated",
                    },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border-2 border-white min-h-18.75 sm:min-h-22.5 flex flex-col justify-center items-center p-2 w-27 sm:w-full"
                      // style={{
                      //   background:
                      //     "linear-gradient(180deg, rgba(255,255,255,0.279) 0%, rgba(255,255,255,0.207) 100%)",
                      // }}
                    >
                      <p className="text-sm sm:text-xl font-bold text-[#10324F]">
                        {item.value}
                      </p>

                      <p className="text-xs sm:text-sm text-[#10324F] leading-tight mt-2">
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <div className="grid grid-cols-2 gap-2 w-full">
                  {dashboardFeatures.map((feature, idx) => {
                    const Icon = feature.icon;

                    return (
                      <div
                        key={idx}
                        className="rounded-lg p-2 sm:p-4 flex items-center gap-2 shadow-sm border-2 border-white"
                        // style={{
                        //   background:
                        //     "linear-gradient(180deg, rgba(255, 255, 255, 0.279) 0%, rgba(255, 255, 255, 0.207) 100%)",
                        // }}
                      >
                        <Icon
                          className="w-6 h-6 text-[#10324F]"
                          aria-hidden="true"
                        />

                        <span className="text-sm text-[#10324F] font-semibold w-36 sm:w-28">
                          {feature.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <StatsSection counts={counts} loading={loading} />
      </section>
      <HRFeatureComparison />

      <section className="PayrollWebApp  min-h-50  bg-[#F6F9FB] w-full py-10 px-6 ">
        {/* Row 2 */}
        <div className="max-w-6xl mx-auto">
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 flex justify-center h-80">
              {/* <img
                src={PayrollWebApp}
                width={364}
                height={300}
                sizes="(max-width: 640px) 364px, 400px"
                loading="eager"
                fetchPriority="high"
                alt="Company setup and employee onboarding interface in Waggex payroll web application"
                className="w-full object-contain block sm:hidden"
              />*/}
              <picture>
                <source media="(max-width: 639px)" srcSet={PayrollWebApp} />
                <img
                  src={PayrollWebAppdesktop}
                  width={388}
                  height={320}
                  loading="eager"
                  decoding="async"
                  sizes="(max-width: 640px) 364px, 400px"
                  fetchPriority="high"
                  alt="Company setup and employee onboarding interface in Waggex payroll web application"
                  className="w-full object-contain"
                />
              </picture>
            </div>

            <div className="order-1 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="service-section-heading">
                  Complete Employee Lifecycle Management Onboarding To Exit
                </h2>
                <p className="service-list-item-text py-4">
                  Every employee journey creates data. Offer letters, onboarding
                  documents, salary revisions, performance records, leave
                  history, and final settlement all of it matters, and all of it
                  needs to be accessible when you need it. Waggex HRMS captures
                  the complete employee lifecycle in one place, so nothing is
                  ever lost in a folder, an inbox, or someone's personal hard
                  drive.
                </p>
                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Digitise employee onboarding in minutes configure roles,
                      departments, salary structures, and issue offer letters
                      without touching paper
                    </span>
                  </li>
                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Maintain centralised, audit-ready employee records from
                      day one to final settlement including PAN, bank details,
                      documents, and revision history
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Row 3 */}
      <section className="PayrollWebApp  min-h-50  w-full py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-1 md:order-1 min-h-30 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="service-section-heading text-[22px]">
                  Smart Leave & Shift Management Automated, Policy-Driven
                </h2>
                <p className="service-list-item-text py-4">
                  Leave disputes and shift confusion are two of the most common
                  HR headaches in Indian businesses. Employees are unsure of
                  their balance, managers are approving leaves on WhatsApp, and
                  payroll is still manually adjusting loss-of-pay at the end of
                  the month. Waggex HRMS eliminates this entirely, leave
                  policies are configured once, approvals are routed
                  automatically, and every balance updates in real time.
                </p>
                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Configure leave types, carryover rules, and approval
                      workflows once Waggex handles all requests, balances, and
                      notifications automatically from there
                    </span>
                  </li>
                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Build shift rosters, manage rotations, and track overtime
                      in a single module that feeds attendance and payroll
                      without a single manual adjustment
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            {/* Row 4 */}
            <div className="order-2 md:order-2 flex justify-center h-80">
              <img
                src={PayrollWebApp3[0].src} // fallback image
                srcSet={`${PayrollWebApp3[0]} 400w,${PayrollWebApp3[1]} 600w`}
                sizes="(max-width: 640px) 100vw,(max-width: 1024px) 50vw,33vw"
                width={364}
                height={300}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                alt="Automated attendance tracking and employee record management in Waggex payroll software"
                className="w-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>
      {/* Row 4 */}
      <section className="PayrollWebApp  min-h-50 bg-[#F6F9FB]  w-full py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Row 4 */}
            <div className="order-1 md:order-1 min-h-30 flex justify-center h-80">
              <img
                src={PayrollWebApp4}
                width="364"
                height="268"
                loading="eager"
                decoding="async"
                sizes="(max-width: 640px) 364px, 400px"
                fetchPriority="high"
                alt="Payroll document generation and compliance management in Waggex HRMS Software"
                className="object-contain block sm:hidden"
              />
              <img
                src={PayrollWebApp4desktop}
                width="400"
                height="295"
                loading="eager"
                decoding="async"
                sizes="(max-width: 640px) 364px, 400px"
                fetchPriority="high"
                alt="Payroll document generation and compliance management in Waggex HRMS Software"
                className="object-contain hidden sm:block"
              />
            </div>
            <div className="order-2 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="service-section-heading text-[22px]">
                  Real-Time HR Analytics & Compliance Reporting
                </h2>
                <p className="service-list-item-text py-4">
                  Most Indian businesses discover a compliance gap when they are
                  already facing a penalty. Waggex HRMS flips that, giving you
                  real-time compliance dashboards, automated reminders for PF
                  deposits and ESI filings, and audit-ready reports that are
                  always just one click away. No scrambling at quarter end. No
                  phone calls to your CA to reconstruct three months of payroll
                  records.
                </p>
                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Get live visibility into headcount, attrition, attendance
                      trends, overtime costs, and leave utilisation all from a
                      single, configurable HR dashboard
                    </span>
                  </li>
                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Generate statutory reports, Form 16, PF ECR files, and ESI
                      contribution statements directly from Waggex formatted and
                      ready for filing with no manual preparation
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* table */}
      <Suspense fallback={null}>
        <ComparisonTable
          headers={headers}
          rows={rows}
          title="What Changes When You Switch to Waggex"
          subtitle="See exactly what moves from painful and manual to automated and instant"
        />
        {/* reviews */}
        <CustomerReviews />

        {/* <FAQ /> */}
        <HomeFaq
          faq1={payrollfaq1}
          faq2={payrollfaq2}
          subtitle="Everything you need to know about Waggex payroll software in India"
        />
        {/*  <AppDownloadCard /> */}
        <PayrollBanner
          heading="Just Need HRMS Software? We Have A Dedicated Page"
          description="Waggex HRMS Software helps you manage payroll, attendance, leaves, employees, shifts, and payslips from one simple platform."
        />
        <AppDownloadCard />
        <ModernizeHR />
      </Suspense>
    </>
  );
}
